import React, { useEffect, useRef, useState } from "react";
import { Upload, FileCheck2, FileWarning, Loader2, Save, Video, ShieldAlert, TrendingUp, Users, Copy } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Section, SectionHeading } from "@/components/blocks";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import XeroPanel, { XeroOrderCell } from "@/components/XeroPanel";
import PaymentHealthPanel from "@/components/PaymentHealthPanel";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import TutorApplicationsPanel from "@/components/TutorApplicationsPanel";
import TestimonialsPanel from "@/components/TestimonialsPanel";
import TutorsPanel from "@/components/TutorsPanel";
import ReviewsPanel from "@/components/ReviewsPanel";

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

function ResourceRow({ r, onUploaded }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const res = await api.post(`/admin/resources/${r.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.title} updated (${res.data.size_kb} KB)`);
      onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 bg-white" data-testid={`admin-resource-${r.id}`}>
      {r.has_file ? <FileCheck2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <FileWarning className="w-5 h-5 text-amber-500 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">{r.title} <span className="text-slate-400 text-xs">({r.id})</span></p>
        <p className="text-xs text-slate-500">{r.price === 0 ? "Free" : `$${r.price}`} · {r.has_file ? `${r.file_type} · ${r.size_kb} KB · updated ${fmt(r.updated_at)}` : "No file uploaded"}</p>
      </div>
      <input ref={inputRef} type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`admin-upload-input-${r.id}`} />
      <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="rounded-full" data-testid={`admin-upload-btn-${r.id}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {r.has_file ? "Replace file" : "Upload file"}
      </Button>
    </div>
  );
}

function RecordingRow({ w, onSaved }) {
  const [url, setUrl] = useState(w.recording_url || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/workshops/${w.id}/recording`, { url });
      toast.success(url ? "Recording link saved" : "Recording link removed");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white" data-testid={`admin-workshop-${w.id}`}>
      <div className="sm:w-64 shrink-0">
        <p className="font-medium text-slate-900 text-sm">{w.title}</p>
        <p className="text-xs text-slate-500">{w.status || "Dates to be announced"}{w.recording_url && <Badge className="ml-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Live</Badge>}</p>
      </div>
      <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtube.com/… or Vimeo link" className="flex-1" data-testid={`admin-recording-input-${w.id}`} />
      <Button size="sm" disabled={busy || url === (w.recording_url || "")} onClick={save} className="bg-slate-900 hover:bg-teal-700 rounded-full" data-testid={`admin-recording-save-${w.id}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
      </Button>
    </div>
  );
}

function SalesPanel({ sales }) {
  const t = sales?.totals || {};
  const cards = [
    { label: "Revenue this month", value: `$${(t.this_month ?? 0).toLocaleString()}`, testId: "sales-month" },
    { label: "Last 7 days", value: `$${(t.last_7_days ?? 0).toLocaleString()}`, testId: "sales-week" },
    { label: "All-time revenue", value: `$${(t.all_time ?? 0).toLocaleString()}`, testId: "sales-alltime" },
    { label: "Orders", value: sales?.order_count ?? 0, testId: "sales-orders" },
    { label: "Average order", value: `$${sales?.average_order ?? 0}`, testId: "sales-average" },
  ];
  return (
    <div data-testid="admin-sales-panel">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <Card key={c.label} className="p-5 border-slate-200" data-testid={`admin-${c.testId}`}>
            <p className="text-xs uppercase tracking-wide text-slate-500">{c.label}</p>
            <p className="font-display text-2xl font-bold text-slate-900 mt-1">{c.value}</p>
          </Card>
        ))}
      </div>
      <div className="mt-6">
        <h4 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-teal-600" /> Top sellers</h4>
        {!sales?.top_items?.length ? (
          <p className="text-slate-500 text-sm" data-testid="admin-top-items-empty">No sales yet — your first order will appear here.</p>
        ) : (
          <div className="space-y-2" data-testid="admin-top-items">
            {sales.top_items.map((i, idx) => {
              const max = sales.top_items[0].revenue || 1;
              return (
                <div key={i.item_id} className="flex items-center gap-4" data-testid={`admin-top-item-${i.item_id}`}>
                  <span className="text-xs text-slate-400 w-5 shrink-0">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-3 text-sm">
                      <span className="font-medium text-slate-800 truncate">{i.item_name}</span>
                      <span className="text-slate-500 shrink-0">{i.units} sold · <span className="font-semibold text-slate-900">${i.revenue}</span></span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full" style={{ width: `${Math.max(4, (i.revenue / max) * 100)}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function WaitlistRow({ row }) {
  const [open, setOpen] = useState(false);
  const pct = row.target ? Math.min(100, Math.round((row.count / row.target) * 100)) : 0;
  const emails = row.people.map((p) => p.email).join(", ");
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`admin-waitlist-${row.workshop_id}`}>
      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900">{row.title}</p>
          <div className="flex items-center gap-3 mt-1.5">
            <div className="h-1.5 w-32 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : "bg-teal-500"}`} style={{ width: `${Math.max(2, pct)}%` }} />
            </div>
            <p className="text-xs text-slate-500" data-testid={`admin-waitlist-count-${row.workshop_id}`}>
              {row.count} registered{row.target ? ` of ${row.target} target` : ""}
            </p>
            {row.target > 0 && row.count >= row.target && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Ready to run</Badge>}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" className="rounded-full" disabled={!row.count} onClick={() => setOpen((o) => !o)} data-testid={`admin-waitlist-toggle-${row.workshop_id}`}>
            <Users className="w-4 h-4" /> {open ? "Hide" : "View people"}
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" disabled={!row.count}
            onClick={() => { navigator.clipboard?.writeText(emails); toast.success(`${row.count} email${row.count === 1 ? "" : "s"} copied`); }}
            data-testid={`admin-waitlist-copy-${row.workshop_id}`}>
            <Copy className="w-4 h-4" /> Copy emails
          </Button>
        </div>
      </div>
      {open && row.count > 0 && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-1.5" data-testid={`admin-waitlist-people-${row.workshop_id}`}>
          {row.people.map((p) => (
            <div key={p.email + p.created_at} className="flex flex-wrap justify-between gap-2 text-sm">
              <span className="text-slate-800">{p.name || "—"} <a href={`mailto:${p.email}`} className="text-teal-700 hover:underline">{p.email}</a></span>
              <span className="text-xs text-slate-400">{fmt(p.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [data, setData] = useState(null);
  const [tutorDraft, setTutorDraft] = useState(null);
  const load = () => api.get("/admin/overview").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  if (loading) return <div className="py-32 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>;
  if (!user || !user.is_admin) {
    return (
      <Section>
        <Card className="max-w-lg mx-auto p-10 text-center border-slate-200" data-testid="admin-denied">
          <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="font-display text-2xl font-bold text-slate-900">Admin access only</h1>
          <p className="text-slate-600 mt-2">{user ? `${user.email} is not an admin account.` : "Sign in with the site owner's Google account to continue."}</p>
          {!user && <Button onClick={login} className="mt-6 bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="admin-signin-btn">Sign in with Google</Button>}
        </Card>
      </Section>
    );
  }

  return (
    <div data-testid="admin-page">
      <div className="bg-slate-900 text-white"><Section className="!py-12"><p className="text-teal-300 text-xs font-semibold tracking-widest uppercase">Admin</p><h1 className="font-display text-3xl font-bold">Site management</h1></Section></div>
      <Section>
        {!data ? <Loader2 className="w-6 h-6 animate-spin text-teal-600" /> : (
          <div className="space-y-16">
            <div>
              <SectionHeading eyebrow="Audience" title="Who's visiting and who's on your list" subtitle="First-party visitor stats plus every person who has signed in, bought or enquired — exportable as CSV." />
              <AnalyticsPanel />
            </div>
            <div>
              <SectionHeading eyebrow="Payments" title="Are payments working right now?" subtitle="Live status of each payment provider, checked directly against Stripe and PayPal." />
              <PaymentHealthPanel />
            </div>
            <div>
              <SectionHeading eyebrow="Sales" title="How the shop is tracking" subtitle="Revenue, best sellers and order volume across your payment providers." />
              <SalesPanel sales={data.sales} />
            </div>
            <div>
              <SectionHeading eyebrow="Tutoring" title="Tutor applications" subtitle="Every applicant with their photo, WWCC or police check and qualifications. Files are private — only your admin account can open them. Use 'Publish as tutor' to turn an approved applicant into a live marketplace profile." />
              <TutorApplicationsPanel onPublish={(d) => { setTutorDraft(d); document.getElementById("admin-tutors")?.scrollIntoView({ behavior: "smooth" }); }} />
            </div>
            <div id="admin-tutors">
              <SectionHeading eyebrow="Marketplace" title="Tutors on your site" subtitle="Publish, edit, hide or remove any tutor profile. Sample profiles can stay visible until you have enough real tutors — just hide them when you're ready." />
              <TutorsPanel draft={tutorDraft} onDraftHandled={() => setTutorDraft(null)} />
            </div>
            <div>
              <SectionHeading eyebrow="Reviews" title="Tutor reviews" subtitle="Families who have booked through the site can leave a star review. Nothing goes live until you approve it — approved reviews become the tutor's public rating." />
              <ReviewsPanel />
            </div>
            <div>
              <SectionHeading eyebrow="Social proof" title="Testimonials" subtitle="Add, edit, hide or remove testimonials yourself. Visible ones appear instantly in the scrolling testimonial band on the site." />
              <TestimonialsPanel />
            </div>
            <div>
              <SectionHeading eyebrow="Workshop interest" title="Who's waiting for each workshop" subtitle="Everyone who registered interest, newest first. Once you hit your target numbers, set a date and email the list." />
              <div className="space-y-3" data-testid="admin-waitlist">
                {data.waitlist?.map((row) => <WaitlistRow key={row.workshop_id} row={row} />)}
              </div>
            </div>
            <div>
              <SectionHeading eyebrow="Digital guides" title="Upload or replace guide files" subtitle="PDF or editable Word (.docx). Buyers always receive the latest file for each guide. Max 25 MB." />
              <div className="grid md:grid-cols-2 gap-3" data-testid="admin-resources">{data.resources.map((r) => <ResourceRow key={r.id} r={r} onUploaded={load} />)}</div>
            </div>
            <div>
              <SectionHeading eyebrow="Workshops" title="Recording links" subtitle="Paste a YouTube/Vimeo link and it appears instantly as 'Watch recording' for every buyer of that workshop." />
              <div className="space-y-3" data-testid="admin-workshops">{data.workshops.map((w) => <RecordingRow key={w.id} w={w} onSaved={load} />)}</div>
            </div>
            <div>
              <SectionHeading eyebrow="Accounting" title="Xero" subtitle="Sync every completed sale into Xero as a customer contact plus a paid invoice." />
              <XeroPanel />
            </div>
            <div>
              <SectionHeading eyebrow="Sales" title="Recent orders" />
              {data.orders.length === 0 ? <p className="text-slate-500 text-sm" data-testid="admin-orders-empty">No completed orders yet.</p> : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm" data-testid="admin-orders">
                    <thead className="bg-slate-50 text-left text-slate-500"><tr><th className="p-3">Date</th><th className="p-3">Item</th><th className="p-3">Buyer</th><th className="p-3">Via</th><th className="p-3">Xero</th><th className="p-3 text-right">Amount</th></tr></thead>
                    <tbody>{data.orders.map((o) => (
                      <tr key={o.id} className="border-t border-slate-100"><td className="p-3 whitespace-nowrap">{fmt(o.captured_at)}</td><td className="p-3">{o.item_name}</td><td className="p-3 text-slate-600">{o.buyer_name} <span className="text-slate-400">{o.buyer_email || o.payer_email}</span></td><td className="p-3 text-slate-500 capitalize">{o.provider || "paypal"}</td><td className="p-3"><XeroOrderCell order={o} onSynced={load} /></td><td className="p-3 text-right font-medium">${o.amount}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
